import type { SupabaseClient } from "@supabase/supabase-js";

// Channels Light (migration 20260808010000_channels_light / PR #207).
//
// Rooms for account, site, and shift-group audiences. Independent message storage (not Feed posts).
// No DMs, threads, reactions, attachments, unread cursor, pin, or acknowledgement in this wave.
// Membership is dynamic on the server; the client never snapshots recipients.

export const LIST_CHANNELS_RPC = "list_account_channels";
export const GET_CHANNEL_RPC = "get_account_channel";
export const CREATE_CHANNEL_RPC = "create_account_channel";
export const ARCHIVE_CHANNEL_RPC = "archive_account_channel";
export const LIST_CHANNEL_MESSAGES_RPC = "list_channel_messages";
export const POST_CHANNEL_MESSAGE_RPC = "post_channel_message";

/** Permission key for create/archive. Reading and posting use dynamic membership. */
export const CHANNELS_MANAGE_PERMISSION = "channel.manage";

/** @deprecated Alias kept for call sites that still import the announcement name. */
export const LIST_CHANNEL_POSTS_RPC = LIST_CHANNEL_MESSAGES_RPC;
/** @deprecated Alias for post_channel_message. */
export const POST_CHANNEL_ANNOUNCEMENT_RPC = POST_CHANNEL_MESSAGE_RPC;

// Board unread cursors (migration 20260812160000_channel_read_cursors, PR #284). These three are
// real server RPCs now; `mark_channel_read` used to be a no-op placeholder here and is not one any
// more. Cursor identity is (account, membership, channel), movement is monotonic, and dynamic
// visibility stays authoritative, so losing an assignment removes that board's unread at once.
export const MARK_CHANNEL_READ_RPC = "mark_channel_read";
export const LIST_MY_UNREAD_CHANNELS_RPC = "list_my_unread_channels";
export const GET_MY_CHANNEL_UNREAD_SUMMARY_RPC = "get_my_channel_unread_summary";

/**
 * One cap for the directory AND the unread list, on purpose.
 *
 * The host sorts both in the same account, site, shift-group order, so an unread limit at least as
 * large as the directory limit is what makes "absent from the unread list means read" a safe
 * inference. Sharing the constant means the two can never drift into a state where a board is
 * listed but its unread row was truncated away, which would silently show a read dot on an unread
 * board.
 */
export const CHANNEL_DIRECTORY_LIMIT = 100;

export const CHANNEL_KINDS = ["account", "site", "shift_group"] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];

export const CHANNEL_GROUP_LABELS: Readonly<Record<ChannelKind, string>> = {
  account: "Company",
  site: "Sites",
  shift_group: "Shift groups",
};

export interface Channel {
  readonly channelId: string;
  readonly kind: ChannelKind;
  readonly name: string;
  readonly siteId: string | null;
  readonly shiftTemplateId: string | null;
  /** Convenience target for the form (site or shift template id). */
  readonly targetId: string | null;
  readonly createdAt: string | null;
  readonly lastMessageAt: string | null;
  /** Always true in Wave 5 Light: product is room-scoped posts, not chat. */
  readonly announceOnly: boolean;
  /**
   * Always false. The directory RPC carries no unread state and never has.
   * Unread lives in its own read: `fetchMyUnreadChannels`, joined by `channelId`.
   */
  readonly unread: boolean;
  /** #207 does not return a member count. */
  readonly memberCount: number;
  /** Removed; use lastMessageAt. */
  readonly lastPostAt: string | null;
  readonly lastPostAuthorDisplayName: string | null;
}

export interface ChannelMessage {
  readonly messageId: string;
  readonly channelId: string;
  readonly body: string;
  readonly authorDisplayName: string;
  readonly notifyOnNextClockIn: boolean;
  readonly sendEmail: boolean;
  readonly sendSms: boolean;
  readonly postedAt: string | null;
}

const CHANNEL_MESSAGE_PAGE_LIMIT = 50;

export const CHANNELS_UNAVAILABLE =
  "Channels are not available on this server yet. Nothing has been posted or missed.";

export const CHANNELS_LOAD_FAILED = "Could not load channels. Check your connection and try again.";

export const CHANNEL_POSTS_LOAD_FAILED =
  "Could not load this channel. Check your connection and try again.";

export const CHANNEL_NO_POSTS = "No posts yet";

export const CHANNEL_ANNOUNCE_ONLY_NOTE = "Room posts only. Membership is decided by the server.";

export const CHANNEL_DM_UNAVAILABLE_NOTE = "Direct messages are not available yet.";

export type ChannelsLoad =
  | { readonly status: "ready"; readonly channels: readonly Channel[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export type ChannelPostsLoad =
  | { readonly status: "ready"; readonly posts: readonly ChannelMessage[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

/** Alias for callers that already say "messages". */
export type ChannelMessagesLoad = ChannelPostsLoad;

const MISSING_SURFACE_CODES: ReadonlySet<string> = new Set(["42883", "42P01", "PGRST202", "PGRST205"]);

export function isMissingChannelSurface(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && MISSING_SURFACE_CODES.has(code)) return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.toLowerCase().includes("could not find the function");
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asChannelKind(value: unknown): ChannelKind | null {
  return typeof value === "string" && (CHANNEL_KINDS as readonly string[]).includes(value)
    ? (value as ChannelKind)
    : null;
}

function asBool(value: unknown): boolean {
  return value === true;
}

/**
 * Parse one channel row from list_account_channels / get_account_channel.
 * column_kind is channel_kind; name is channel_name.
 */
export function mapChannel(raw: unknown): Channel | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const channelId = asText(row.channel_id);
  if (channelId === null) return null;
  const kind = asChannelKind(row.channel_kind ?? row.kind);
  if (kind === null) return null;
  const name = asText(row.channel_name ?? row.name);
  if (name === null) return null;
  const siteId = asText(row.site_id);
  const shiftTemplateId = asText(row.shift_template_id);
  const targetId =
    kind === "site" ? siteId : kind === "shift_group" ? shiftTemplateId : asText(row.target_id);
  const lastMessageAt = asText(row.last_message_at) ?? asText(row.last_post_at);
  return {
    channelId,
    kind,
    name,
    siteId,
    shiftTemplateId,
    targetId,
    createdAt: asText(row.created_at),
    lastMessageAt,
    announceOnly: true,
    unread: false,
    memberCount: 0,
    lastPostAt: lastMessageAt,
    lastPostAuthorDisplayName: null,
  };
}

export function mapChannels(raw: unknown): Channel[] {
  if (!Array.isArray(raw)) return [];
  const channels: Channel[] = [];
  for (const row of raw) {
    const channel = mapChannel(row);
    if (channel !== null) channels.push(channel);
  }
  return channels;
}

export function mapChannelMessage(raw: unknown): ChannelMessage | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const messageId = asText(row.message_id);
  if (messageId === null) return null;
  const channelId = asText(row.channel_id);
  if (channelId === null) return null;
  return {
    messageId,
    channelId,
    body: asText(row.body) ?? "",
    authorDisplayName: asText(row.author_display_name) ?? "SecOps",
    notifyOnNextClockIn: asBool(row.notify_on_next_clock_in),
    sendEmail: asBool(row.send_email),
    sendSms: asBool(row.send_sms),
    postedAt: asText(row.posted_at),
  };
}

export function mapChannelMessages(raw: unknown): ChannelMessage[] {
  if (!Array.isArray(raw)) return [];
  const messages: ChannelMessage[] = [];
  for (const row of raw) {
    const message = mapChannelMessage(row);
    if (message !== null) messages.push(message);
  }
  return messages;
}

export interface ChannelGroup {
  readonly kind: ChannelKind;
  readonly label: string;
  readonly channels: readonly Channel[];
}

export function groupChannels(channels: readonly Channel[]): ChannelGroup[] {
  const groups: ChannelGroup[] = [];
  for (const kind of CHANNEL_KINDS) {
    const inKind = channels
      .filter((channel) => channel.kind === kind)
      .sort((left, right) => left.name.localeCompare(right.name));
    if (inKind.length > 0) {
      groups.push({ kind, label: CHANNEL_GROUP_LABELS[kind], channels: inKind });
    }
  }
  return groups;
}

export function channelSubtitle(
  channel: Channel,
  formatWhen: (iso: string) => string | null,
): string {
  if (channel.lastMessageAt === null && channel.lastPostAt === null) {
    return CHANNEL_NO_POSTS;
  }
  const whenIso = channel.lastMessageAt ?? channel.lastPostAt;
  if (whenIso === null) return CHANNEL_NO_POSTS;
  return formatWhen(whenIso) ?? CHANNEL_NO_POSTS;
}

/**
 * Always none, deliberately, even now that cursors exist.
 *
 * `Channel` rows carry no unread state, so this cannot answer from them, and it must not start
 * guessing. A shell that wants a dot reads `fetchMyUnreadChannels` and joins by channel id. This
 * export stays as the thing that refuses to invent a count.
 */
export function channelUnreadBadge(_channels: readonly Channel[]): "dot" | "none" {
  return "none";
}

export function sortChannelMessages(messages: readonly ChannelMessage[]): ChannelMessage[] {
  return [...messages].sort((left, right) => postedTime(right) - postedTime(left));
}

/** @deprecated Prefer sortChannelMessages. */
export function sortChannelPosts(posts: readonly ChannelMessage[]): ChannelMessage[] {
  return sortChannelMessages(posts);
}

function postedTime(message: ChannelMessage): number {
  const parsed = Date.parse(message.postedAt ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function fetchChannels(client: SupabaseClient, accountId: string): Promise<ChannelsLoad> {
  try {
    const { data, error } = await client.rpc(LIST_CHANNELS_RPC, {
      p_account_id: accountId,
      p_limit: CHANNEL_DIRECTORY_LIMIT,
    });
    if (error === null) {
      return { status: "ready", channels: mapChannels(data) };
    }
    if (isMissingChannelSurface(error)) {
      return { status: "unavailable", reason: CHANNELS_UNAVAILABLE };
    }
    // Prefer host exception text when it is a known membership/permission sentence so
    // switcher-without-membership is not misread as an offline/network problem.
    const hostMessage = (error as { message?: unknown }).message;
    if (typeof hostMessage === "string" && KNOWN_CHANNEL_FAILURES.has(hostMessage)) {
      return { status: "failed", reason: hostMessage };
    }
    return { status: "failed", reason: CHANNELS_LOAD_FAILED };
  } catch {
    return { status: "failed", reason: CHANNELS_LOAD_FAILED };
  }
}

export async function fetchChannelPosts(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
): Promise<ChannelPostsLoad> {
  return fetchChannelMessages(client, accountId, channelId);
}

export async function fetchChannelMessages(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
): Promise<ChannelPostsLoad> {
  try {
    const { data, error } = await client.rpc(LIST_CHANNEL_MESSAGES_RPC, {
      p_account_id: accountId,
      p_channel_id: channelId,
      p_limit: CHANNEL_MESSAGE_PAGE_LIMIT,
    });
    if (error === null) {
      return { status: "ready", posts: sortChannelMessages(mapChannelMessages(data)) };
    }
    if (isMissingChannelSurface(error)) {
      return { status: "unavailable", reason: CHANNELS_UNAVAILABLE };
    }
    return { status: "failed", reason: CHANNEL_POSTS_LOAD_FAILED };
  } catch {
    return { status: "failed", reason: CHANNEL_POSTS_LOAD_FAILED };
  }
}

// --- board unread cursors ------------------------------------------------------------------

export interface ChannelUnread {
  readonly channelId: string;
  readonly latestMessageId: string | null;
  readonly latestMessageAt: string | null;
  readonly lastOpenedAt: string | null;
  readonly unreadMessageCount: number;
}

export const CHANNEL_UNREAD_UNAVAILABLE =
  "Board unread is not available on this server yet.";

export const CHANNEL_UNREAD_LOAD_FAILED =
  "Could not load which boards have something new.";

export type ChannelUnreadLoad =
  | { readonly status: "ready"; readonly unread: readonly ChannelUnread[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export function mapChannelUnread(raw: unknown): ChannelUnread | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const channelId = asText(row.channel_id);
  if (channelId === null) return null;
  const count = Number(row.unread_message_count);
  return {
    channelId,
    latestMessageId: asText(row.latest_message_id),
    latestMessageAt: asText(row.latest_message_at),
    lastOpenedAt: asText(row.last_opened_at),
    unreadMessageCount: Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0,
  };
}

export function mapChannelUnreadRows(raw: unknown): ChannelUnread[] {
  if (!Array.isArray(raw)) return [];
  const rows: ChannelUnread[] = [];
  for (const item of raw) {
    const parsed = mapChannelUnread(item);
    if (parsed !== null) rows.push(parsed);
  }
  return rows;
}

/**
 * Boards with something this membership has not read, newest-visible first in the same order the
 * directory uses. The host already filters to currently visible, active boards with a non-zero
 * unread count, so every returned id is a dot.
 *
 * Never throws: a server without the RPC reads as `unavailable` so the caller can fall back or say
 * so, rather than showing a board list that silently looks caught up.
 */
export async function fetchMyUnreadChannels(
  client: SupabaseClient,
  accountId: string,
  limit: number = CHANNEL_DIRECTORY_LIMIT,
): Promise<ChannelUnreadLoad> {
  try {
    const { data, error } = await client.rpc(LIST_MY_UNREAD_CHANNELS_RPC, {
      p_account_id: accountId,
      p_limit: limit,
    });
    if (error === null) {
      return { status: "ready", unread: mapChannelUnreadRows(data) };
    }
    if (isMissingChannelSurface(error)) {
      return { status: "unavailable", reason: CHANNEL_UNREAD_UNAVAILABLE };
    }
    const hostMessage = (error as { message?: unknown }).message;
    if (typeof hostMessage === "string" && KNOWN_CHANNEL_FAILURES.has(hostMessage)) {
      return { status: "failed", reason: hostMessage };
    }
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  } catch {
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  }
}

/** The ids a client turns into dots. Empty when unread could not be read from the server. */
export function unreadChannelIdSet(load: ChannelUnreadLoad): ReadonlySet<string> {
  if (load.status !== "ready") return new Set();
  return new Set(load.unread.map((row) => row.channelId));
}

export const SAFE_CHANNEL_FAILURE = "Couldn't complete that channel action - please try again.";

/** Verbatim sentences from qa/2026-08-07-prd-001-channels-light-db-contract.md */
export const KNOWN_CHANNEL_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to access Channels.",
  "Account id is required to access Channels.",
  "Active account membership is required to access Channels.",
  "Join this account as admin or start a support session to create or post in Channels. Platform superadmin alone cannot author channel rows without a membership.",
  "Channel management permission is required.",
  "Channel id is required.",
  "Channel was not found or is not available.",
  "Channel was not found.",
  "Channel list limit must be between 1 and 100.",
  "Channel message list limit must be between 1 and 100.",
  "Channel name must be between 1 and 120 characters.",
  "Channel kind is not supported.",
  "Account channels cannot have a site or shift group target.",
  "Site channels require only a site target.",
  "Channel site target was not found in this account.",
  "Shift-group channels require only a shift template target.",
  "Channel shift group target was not found in this account.",
  "An active channel with this name already exists.",
  "An active channel already exists for this target.",
  "Channel message cursor was not found.",
  // Board unread cursors (PR #284).
  "An active account membership or support session is required for personal Channel unread state.",
  "Channel unread list limit must be between 1 and 100.",
  "Channel message body must be between 1 and 10000 characters.",
  "Channel notification options must be true or false.",
  "Channel post rate limit exceeded. Try again later.",
]);

export function mapChannelFailure(message: unknown): string {
  return typeof message === "string" && KNOWN_CHANNEL_FAILURES.has(message)
    ? message
    : SAFE_CHANNEL_FAILURE;
}

export type ChannelWriteOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

async function runChannelWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<ChannelWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null
      ? { ok: true }
      : { ok: false, reason: mapChannelFailure((error as { message?: unknown }).message) };
  } catch {
    return { ok: false, reason: SAFE_CHANNEL_FAILURE };
  }
}

/**
 * Record that this membership has read a board, up to and including one message.
 *
 * Call it AFTER the board detail has loaded successfully, and pass the newest message id actually
 * rendered. Passing null on a non-empty board marks through the server's current latest message,
 * which is stronger than "what the officer saw": a message that arrived between the read and the
 * mark would be marked read without ever being on screen. Null is correct only for a board that
 * loaded successfully and was empty.
 *
 * Repeat calls are safe. The host cursor is monotonic, so an older id never rewinds a newer one.
 */
export async function markChannelRead(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
  throughMessageId: string | null,
): Promise<ChannelWriteOutcome> {
  return runChannelWrite(client, MARK_CHANNEL_READ_RPC, {
    p_account_id: accountId,
    p_channel_id: channelId,
    p_through_message_id: throughMessageId,
  });
}

export interface ChannelDraft {
  readonly kind: ChannelKind;
  readonly name: string;
  readonly targetId: string | null;
}

export const EMPTY_CHANNEL_DRAFT: ChannelDraft = { kind: "account", name: "", targetId: null };

export function validateChannelDraft(draft: ChannelDraft): { ok: true } | { ok: false; reason: string } {
  if (draft.name.trim().length === 0) return { ok: false, reason: "Give the channel a name." };
  if (draft.name.trim().length > 120) {
    return { ok: false, reason: "Channel names are 120 characters or fewer." };
  }
  if (draft.kind !== "account" && (draft.targetId ?? "").trim().length === 0) {
    return { ok: false, reason: "Choose what this channel is for." };
  }
  return { ok: true };
}

export async function createChannel(
  client: SupabaseClient,
  accountId: string,
  draft: ChannelDraft,
): Promise<ChannelWriteOutcome> {
  const validated = validateChannelDraft(draft);
  if (!validated.ok) return validated;
  const target = draft.kind === "account" ? null : (draft.targetId ?? "").trim() || null;
  return runChannelWrite(client, CREATE_CHANNEL_RPC, {
    p_account_id: accountId,
    p_name: draft.name.trim(),
    p_kind: draft.kind,
    p_site_id: draft.kind === "site" ? target : null,
    p_shift_template_id: draft.kind === "shift_group" ? target : null,
  });
}

export async function archiveChannel(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
): Promise<ChannelWriteOutcome> {
  if (channelId.trim().length === 0) return { ok: false, reason: SAFE_CHANNEL_FAILURE };
  return runChannelWrite(client, ARCHIVE_CHANNEL_RPC, {
    p_account_id: accountId,
    p_channel_id: channelId.trim(),
  });
}

/** Body-only message draft (no title/pin/ack; those are Feed). */
export interface ChannelAnnouncementDraft {
  readonly title: string;
  readonly body: string;
  readonly isPinned: boolean;
  readonly requiresAck: boolean;
  readonly notifyOnNextClockIn: boolean;
  readonly sendEmail: boolean;
  readonly sendSms: boolean;
}

export const EMPTY_CHANNEL_ANNOUNCEMENT_DRAFT: ChannelAnnouncementDraft = {
  title: "",
  body: "",
  isPinned: false,
  requiresAck: false,
  notifyOnNextClockIn: false,
  sendEmail: false,
  sendSms: false,
};

export function validateChannelAnnouncement(
  draft: ChannelAnnouncementDraft | { readonly body: string },
): { ok: true } | { ok: false; reason: string } {
  if (draft.body.trim().length === 0) return { ok: false, reason: "Write the message." };
  if (draft.body.trim().length > 10000) {
    return { ok: false, reason: "Messages are 10000 characters or fewer." };
  }
  return { ok: true };
}

export async function postChannelAnnouncement(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
  draft: ChannelAnnouncementDraft | { readonly body: string },
): Promise<ChannelWriteOutcome> {
  return postChannelMessage(client, accountId, channelId, draft);
}

export async function postChannelMessage(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
  draft:
    | ChannelAnnouncementDraft
    | {
        readonly body: string;
        readonly notifyOnNextClockIn?: boolean;
        readonly sendEmail?: boolean;
        readonly sendSms?: boolean;
      },
): Promise<ChannelWriteOutcome> {
  const validated = validateChannelAnnouncement(draft);
  if (!validated.ok) return validated;
  if (channelId.trim().length === 0) return { ok: false, reason: SAFE_CHANNEL_FAILURE };
  const full = draft as Partial<ChannelAnnouncementDraft>;
  return runChannelWrite(client, POST_CHANNEL_MESSAGE_RPC, {
    p_account_id: accountId,
    p_channel_id: channelId.trim(),
    p_body: draft.body.trim(),
    p_notify_on_next_clock_in: full.notifyOnNextClockIn === true,
    p_send_email: full.sendEmail === true,
    p_send_sms: full.sendSms === true,
  });
}

// The #207 no-op stub that used to live here is gone: `mark_channel_read` is a real RPC as of
// migration 20260812160000, and the implementation is with the other cursor functions above.
